import { inject } from '@angular/core';
import { type CanActivateFn } from '@angular/router';
import type { Account } from '@frontendlib/common/types/account';
import { AccountService } from '@frontendlib/common/services/account.service';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import {
  MAIN_SPACE_CONTEXT_VARIANT,
  readSpaceContextSlot,
  writeSpaceContextSlot,
} from '@frontendlib/common/utils/space-context-storage';
import { catchError, map, type Observable, of, take, timeout } from 'rxjs';

/**
 * How long boot waits for the one identity read before it gives up and lets the app through.
 *
 * The same bound, and for the same reason, as `AppTenantScopeService`'s: a lookup that FAILS is
 * handled below, but one that never answers would otherwise hold the whole app on a blank screen —
 * strictly worse than the defect this guard closes. Ten seconds is far longer than the one cached
 * GET involved, so a healthy stack never reaches it.
 */
const ACCOUNT_KIND_TIMEOUT_MS = 10_000;

/**
 * A TEAM account never boots into a PERSONAL context, whatever the address or the slot says.
 *
 * THE DEFECT (#1303). `/` names no workspace, so `SpaceContextStore` falls through to the remembered
 * slot — and that slot is removed by any PERSONAL boot in any account on this browser. A team
 * account therefore resolved PERSONAL, `effectiveTenantId()` answered `null`, the request carried no
 * `space_id`, and the backend stamped the session's own account id: work went into `users.user_id`,
 * a place with neither My Workspace nor Team Workspace. ROW 2a stops that for the profile-menu
 * switch by landing on the space's own address; this guard is what makes it true for every OTHER way
 * in — a reload on `/mail`, a bookmark, a restored tab.
 *
 * WHY A GUARD AND NOT A PATCH INSIDE THE STORE. The account's kind can only be read fresh from the
 * server and that read is asynchronous, so seeding a guess and correcting it afterwards is precisely
 * the failure ADR-0005 exists to prevent — a flash of the wrong space and a round of requests
 * against a tenant nobody asked for. The mechanism that withholds rather than guesses is already
 * here: Angular does not activate a route until its guard resolves, and `*spaceContext="'main'"`
 * lives INSIDE `layout.component.html`, so `SpaceContextStore` is constructed strictly after this
 * returns. There is no window in which a wrong context exists, so there is nothing to correct.
 *
 * IT FAILS OPEN, deliberately and in every branch: a person whose account read fails keeps their
 * app, on whatever context the slot already said. The server enforces tenancy regardless, and a
 * guard that can lock someone out of a working product to protect a default is the wrong trade —
 * the same call `checkoutRequiredGuard` makes next door.
 */
export const teamSpaceBootGuard: CanActivateFn = () => {
  const accountService = inject(AccountService);
  const currentUserStore = inject(CurrentUserStore);

  return accountService.getAccount().pipe(
    take(1),
    timeout(ACCOUNT_KIND_TIMEOUT_MS),
    map((account: Account): boolean => {
      seedSpaceContextFor(account, currentUserStore);
      return true;
    }),
    catchError((): Observable<boolean> => of(true)),
  );
};

/**
 * Writes what the store is about to read: the fresh account id, and the space its context belongs
 * in.
 *
 * THE ACCOUNT ID HAS TO COME FROM HERE, and that is measured rather than assumed. `CurrentUserStore`
 * is seeded from `localStorage.authPayload`, and nothing rewrites that key after
 * `POST /api/account/sessions/switch` — so after an in-app switch it names the account just LEFT,
 * and the switch's own document reload re-reads the same stale value. Keying a per-account slot on
 * it would file every account's memory under its predecessor. This read is the first fresh answer
 * the document has, so it is the one that seeds the store and the slot alike.
 */
function seedSpaceContextFor(account: Account, currentUserStore: InstanceType<typeof CurrentUserStore>): void {
  if (account.id) {
    currentUserStore.setAccountId(account.id);
  }

  if (account.homeSpaceId) {
    // A team account has exactly one home, and it is not a matter of what was remembered.
    writeSpaceContextSlot(account.homeSpaceId, MAIN_SPACE_CONTEXT_VARIANT, account.id);
    return;
  }

  // A personal account keeps whatever it was in — including a shared space it is a member of. The
  // write is the migration: a value still under the legacy origin-wide key moves to this account's
  // own slot, so the next personal boot in a DIFFERENT account can no longer clear it.
  const remembered = readSpaceContextSlot(MAIN_SPACE_CONTEXT_VARIANT, account.id);
  writeSpaceContextSlot(remembered ?? undefined, MAIN_SPACE_CONTEXT_VARIANT, account.id);
}
